import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PopularVehiclesService } from 'src/app/services/popular-vehicles/popular-vehicles.service';
import { PopularVehiclesModalService } from 'src/app/services/popular-vehicles-modal/popular-vehicles-modal.service';
import { CarMakerService } from 'src/app/services/car-maker/car-maker.service';
import { CarModelService } from 'src/app/services/car-model/car-model.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-popular-vehicles',
  templateUrl: './add-edit-popular-vehicles.component.html',
  styleUrls: ['./add-edit-popular-vehicles.component.scss']
})
export class AddEditPopularVehiclesComponent extends DestructorComponent implements OnInit {

  @Output() popularVehicleAdded = new EventEmitter<void>();
  @Output() popularVehicleUpdated = new EventEmitter<void>();

  popularVehicleForm!: FormGroup;
  isLoading = false;
  isEditMode = false;
  selectedPopularVehicle: any = null;

  // Make search properties
  makeSearchTerm: string = '';
  makeSearchResults: any[] = [];
  selectedMake: any = null;
  showMakeDropdown: boolean = false;
  isMakeSearching: boolean = false;
  private makeSearchSubject = new Subject<string>();

  // Model search properties
  modelSearchTerm: string = '';
  modelSearchResults: any[] = [];
  selectedModel: any = null;
  showModelDropdown: boolean = false;
  isModelSearching: boolean = false;
  private modelSearchSubject = new Subject<string>();

  constructor(
    private fb: FormBuilder,
    private _popularVehiclesService: PopularVehiclesService,
    private _popularVehiclesModalService: PopularVehiclesModalService,
    private _carMakerService: CarMakerService,
    private _carModelService: CarModelService
  ) {
    super();
    this.initializeForm();
    this.setupMakeSearchDebounce();
    this.setupModelSearchDebounce();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.popularVehicleForm = this.fb.group({
      make: ['', [Validators.required]],
      model: [{ value: '', disabled: true }, [Validators.required]]
    });
  }

  get make() { return this.popularVehicleForm.get('make'); }
  get model() { return this.popularVehicleForm.get('model'); }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_popular_vehicle_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const servicePopularVehicle = this._popularVehiclesModalService.getSelectedPopularVehicle();
        if (servicePopularVehicle) {
          this.isEditMode = true;
          this.selectedPopularVehicle = servicePopularVehicle;
          this.populateForm(this.selectedPopularVehicle);
        } else {
          this.isEditMode = false;
          this.selectedPopularVehicle = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._popularVehiclesModalService.clearSelectedPopularVehicle();
        this.resetForm();
      });
    }
  }

  private populateForm(popularVehicle: any): void {
    if (popularVehicle.make) {
      this.selectedMake = popularVehicle.make;
      this.makeSearchTerm = popularVehicle.make.name || '';
      this.popularVehicleForm.patchValue({
        make: popularVehicle.make._id || popularVehicle.make.id
      });

      // Enable model field when make is populated
      this.model?.enable();
    }

    if (popularVehicle.model) {
      this.selectedModel = popularVehicle.model;
      this.modelSearchTerm = popularVehicle.model.name || '';
      this.popularVehicleForm.patchValue({
        model: popularVehicle.model._id || popularVehicle.model.id
      });
    }
  }

  private resetForm(): void {
    this.popularVehicleForm.reset();
    this.selectedMake = null;
    this.selectedModel = null;
    this.makeSearchTerm = '';
    this.modelSearchTerm = '';
    this.makeSearchResults = [];
    this.modelSearchResults = [];
    this.showMakeDropdown = false;
    this.showModelDropdown = false;
    this.isLoading = false;

    // Disable model field on reset
    this.model?.disable();

    this.markFormGroupUntouched(this.popularVehicleForm);
  }

  private markFormGroupUntouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsUntouched();
      control?.markAsPristine();
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_popular_vehicle_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  // Make search methods
  private setupMakeSearchDebounce(): void {
    this.makeSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchMakes(searchTerm);
    });
  }

  onMakeSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.makeSearchTerm = target.value;

    if (this.makeSearchTerm.length >= 2) {
      this.makeSearchSubject.next(this.makeSearchTerm);
      this.showMakeDropdown = true;
    } else {
      this.showMakeDropdown = false;
      this.makeSearchResults = [];
    }
  }

  onMakeInputFocus(): void {
    if (this.makeSearchResults.length > 0) {
      this.showMakeDropdown = true;
    }
  }

  onMakeInputBlur(): void {
    setTimeout(() => {
      this.showMakeDropdown = false;
    }, 200);
  }

  private searchMakes(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.makeSearchResults = [];
      this.showMakeDropdown = false;
      return;
    }

    this.isMakeSearching = true;
    this.subscriptions.push(
      this._carMakerService.GetData('1', searchTerm, { paginate: 'false' }).subscribe({
        next: (response: any) => {
          this.isMakeSearching = false;
          if (response.status && response.data) {
            this.makeSearchResults = response.data.data || response.data || [];
            this.showMakeDropdown = this.makeSearchResults.length > 0;
          } else {
            this.makeSearchResults = [];
            this.showMakeDropdown = false;
          }
        },
        error: (error: any) => {
          this.isMakeSearching = false;
          console.error('Error searching makes:', error);
          this.makeSearchResults = [];
          this.showMakeDropdown = false;
        }
      })
    );
  }

  selectMake(make: any): void {
    this.selectedMake = make;
    this.makeSearchTerm = make.name;
    this.popularVehicleForm.patchValue({
      make: make._id || make.id
    });
    this.makeSearchResults = [];
    this.showMakeDropdown = false;

    // Enable model field when make is selected
    this.model?.enable();

    // Clear model selection when make changes
    this.clearModel();
  }

  clearMake(): void {
    this.selectedMake = null;
    this.makeSearchTerm = '';
    this.popularVehicleForm.patchValue({ make: '' });

    // Disable model field and clear model when make is cleared
    this.model?.disable();
    this.clearModel();
  }

  // Model search methods
  private setupModelSearchDebounce(): void {
    this.modelSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchModels(searchTerm);
    });
  }

  onModelSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.modelSearchTerm = target.value;

    if (this.modelSearchTerm.length >= 2) {
      this.modelSearchSubject.next(this.modelSearchTerm);
      this.showModelDropdown = true;
    } else {
      this.showModelDropdown = false;
      this.modelSearchResults = [];
    }
  }

  onModelInputFocus(): void {
    if (this.modelSearchResults.length > 0) {
      this.showModelDropdown = true;
    }
  }

  onModelInputBlur(): void {
    setTimeout(() => {
      this.showModelDropdown = false;
    }, 200);
  }

  private searchModels(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.modelSearchResults = [];
      this.showModelDropdown = false;
      return;
    }

    // Don't search if make is not selected
    if (!this.selectedMake || !this.selectedMake.slug) {
      this.modelSearchResults = [];
      this.showModelDropdown = false;
      return;
    }

    this.isModelSearching = true;

    // Include make slug in the filters
    const filters = {
      paginate: 'false',
      make: this.selectedMake.slug
    };

    this.subscriptions.push(
      this._carModelService.GetData('1', searchTerm, filters).subscribe({
        next: (response: any) => {
          this.isModelSearching = false;
          if (response.status && response.data) {
            this.modelSearchResults = response.data.data || response.data || [];
            this.showModelDropdown = this.modelSearchResults.length > 0;
          } else {
            this.modelSearchResults = [];
            this.showModelDropdown = false;
          }
        },
        error: (error: any) => {
          this.isModelSearching = false;
          console.error('Error searching models:', error);
          this.modelSearchResults = [];
          this.showModelDropdown = false;
        }
      })
    );
  }

  selectModel(model: any): void {
    this.selectedModel = model;
    this.modelSearchTerm = model.name;
    this.popularVehicleForm.patchValue({
      model: model._id || model.id
    });
    this.modelSearchResults = [];
    this.showModelDropdown = false;
  }

  clearModel(): void {
    this.selectedModel = null;
    this.modelSearchTerm = '';
    this.popularVehicleForm.patchValue({ model: '' });
  }

  onSubmit(): void {
    if (this.popularVehicleForm.valid && !this.isLoading) {
      this.isLoading = true;

      const submitData: any = {
        make: this.selectedMake?._id || this.selectedMake?.id,
        model: this.selectedModel?._id || this.selectedModel?.id
      };

      const apiCall = this.isEditMode
        ? this._popularVehiclesService.UpdateData(this.selectedPopularVehicle._id, submitData)
        : this._popularVehiclesService.AddData(submitData);

      this.subscriptions.push(
        apiCall.subscribe({
          next: (response: any) => {
            this.isLoading = false;

            Swal.fire({
              text: this.isEditMode
                ? "Popular vehicle has been successfully updated!"
                : "Popular vehicle has been successfully created!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                this.closeModal();

                if (this.isEditMode) {
                  this.popularVehicleUpdated.emit();
                } else {
                  this.popularVehicleAdded.emit();
                }

                this.resetForm();
              }
            });
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('Popular vehicle operation failed:', error);

            Swal.fire({
              text: error.error?.message || "An error occurred. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    } else {
      this.markFormGroupTouched(this.popularVehicleForm);
    }
  }
}
